import { ShieldQuestion } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import ProfileFieldList from "./ProfileFieldList";
import { mediaUrl } from "../lib/api";
import { initialsFrom } from "../lib/utils";
import {
  COACH_PROFILE_UNVERIFIED_NOTE,
  hasCoachProfileContent,
  levelPlayedLabel,
  specialismLabel,
} from "../lib/coachProfile";

/**
 * Read-only rendering of a coach's profile (issue #290) — the ONE component
 * every reader of a coach profile goes through.
 *
 * Two readers today, both in Settings: the player's Coach access card, for
 * an active pairing and for a still-pending invite alike. (`UnverifiedNote`
 * has a third caller — CoachProfileCard renders it above the coach's own
 * form.) The coach's read-only mirror on `/profile` was the third reader
 * until #338 merged that page into Settings and dropped the mirror rather
 * than sit it inches below the form it mirrored. One component for every
 * rendering is what makes the honesty line structural rather than
 * remembered: `UnverifiedNote` is rendered here, not by any caller, so a
 * new surface cannot ship without it by forgetting a line.
 *
 * Empty fields are omitted, not rendered as an em dash. Every field is
 * optional (see schema.ts), so a coach who filled in two of eight should
 * read as a short profile, not a mostly-blank form — and "Qualifications:
 * —" on a coach's promotional profile reads as an absence the platform is
 * pointing at.
 *
 * `compact` is the player-side inline variant (inside a link row, under a
 * name and email): smaller type, no headings. Same fields, same note.
 *
 * THE PHOTO (#478) renders in both variants, through `mediaUrl` (a data:
 * URL passes through unchanged). The fallback is INITIALS — from the
 * optional `name` prop, which the player-side caller has from the link row
 * — and never, under any refactor, a stock photograph: the photo is a
 * self-asserted claim like every other field here, and the unverified note
 * keeps applying to it. When there is no photo at all the avatar is
 * omitted rather than rendered as an initials disc: every surface that
 * embeds this view already shows the coach's name immediately above it,
 * and a decorative monogram would just restate it.
 */
export default function CoachProfileView({ profile, compact = false, testId, name }) {
  if (!hasCoachProfileContent(profile)) return null;

  const years = profile.years_coaching;
  const facts = [
    years ? { label: "Coaching for", value: `${years} ${years === 1 ? "year" : "years"}` } : null,
    profile.highest_level_played
      ? { label: "Highest level played", value: levelPlayedLabel(profile.highest_level_played) }
      : null,
    profile.region ? { label: "Region", value: profile.region } : null,
    profile.qualifications ? { label: "Qualifications", value: profile.qualifications } : null,
    profile.teams_coached ? { label: "Teams coached", value: profile.teams_coached } : null,
  ].filter(Boolean);

  return (
    <div className={`min-w-0 ${compact ? "space-y-2" : "space-y-3"}`} data-testid={testId}>
      {profile.photo && (
        <Avatar
          className={`${compact ? "h-10 w-10" : "h-14 w-14"} border-2 border-[#D4A017]`}
          data-testid={testId ? `${testId}-photo` : undefined}
        >
          <AvatarImage src={mediaUrl(profile.photo)} alt="" />
          <AvatarFallback className="text-xs font-semibold">
            {initialsFrom(name) || "C"}
          </AvatarFallback>
        </Avatar>
      )}
      {profile.headline && (
        <p
          className={`font-semibold text-slate-900 break-words ${compact ? "text-sm" : "text-base"}`}
          data-testid={testId ? `${testId}-headline` : undefined}
        >
          {profile.headline}
        </p>
      )}

      {profile.specialisms?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={testId ? `${testId}-specialisms` : undefined}>
          {profile.specialisms.map((s) => (
            <span
              key={s}
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold break-words"
              style={{ background: "#84C5FF33", color: "#0B2E59" }}
            >
              {specialismLabel(s)}
            </span>
          ))}
        </div>
      )}

      <ProfileFieldList facts={facts} compact={compact} />

      {profile.about && (
        <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">{profile.about}</p>
      )}

      <UnverifiedNote testId={testId ? `${testId}-unverified` : undefined} />
    </div>
  );
}

/**
 * The line #290 §2 requires on every rendering, and the reason this file
 * owns it. Exported so the Settings editor can carry it too — the coach
 * should see the exact framing their claims are shown in before they write
 * them, not discover it afterwards.
 *
 * Deliberately quiet: slate, small, an outline icon. It is a standing fact
 * about the platform, not a warning about this particular coach, and
 * styling it as a warning would make an honest disclosure read as an
 * accusation.
 */
export function UnverifiedNote({ testId, className = "" }) {
  return (
    <p
      className={`flex items-start gap-1.5 text-[11px] text-slate-500 min-w-0 ${className}`}
      data-testid={testId}
    >
      <ShieldQuestion size={13} className="shrink-0 mt-px" />
      <span className="break-words">{COACH_PROFILE_UNVERIFIED_NOTE}</span>
    </p>
  );
}
